export type GlobalSearchHitKind =
  | 'action'
  | 'recent'
  | 'patient'
  | 'appointment'
  | 'invoice'
  | 'viewAllInvoices'
  | 'viewAllAppointments';

export interface GlobalSearchActionHit {
  kind: 'action';
  id: string;
  label: string;
  description: string;
  href: string;
  icon: 'userPlus' | 'users' | 'calendar' | 'receipt';
}

export interface GlobalSearchRecentHit {
  kind: 'recent';
  /** Keyboard / DOM id — unique among flat hits. */
  id: string;
  entityId: string;
  /** Original entity kind stored in localStorage. */
  entityKind: 'patient' | 'appointment' | 'invoice';
  label: string;
  href: string;
  at: number;
}

export interface GlobalSearchPatientHit {
  kind: 'patient';
  id: string;
  name: string;
  phone?: string;
  patientDisplayId?: string;
  bloodGroup?: string | null;
  href: string;
}

export interface GlobalSearchAppointmentHit {
  kind: 'appointment';
  id: string;
  patientName: string;
  providerName?: string | null;
  scheduledAt: string;
  status: string;
  href: string;
}

export interface GlobalSearchInvoiceHit {
  kind: 'invoice';
  id: string;
  displayNumber: string;
  patientName?: string;
  patientDisplayId?: string;
  status: string;
  totalPaise: number;
  href: string;
}

export interface GlobalSearchViewAllHit {
  kind: 'viewAllInvoices' | 'viewAllAppointments';
  id: 'view-all-invoices' | 'view-all-appointments';
  label: string;
  href: string;
}

export type GlobalSearchHit =
  | GlobalSearchActionHit
  | GlobalSearchRecentHit
  | GlobalSearchPatientHit
  | GlobalSearchAppointmentHit
  | GlobalSearchInvoiceHit
  | GlobalSearchViewAllHit;

/** Persisted recent destination (no phones). */
export interface GlobalSearchRecentStored {
  id: string;
  kind: 'patient' | 'appointment' | 'invoice';
  label: string;
  href: string;
  at: number;
}

export const GLOBAL_SEARCH_RECENTS_MAX = 5;
export const GLOBAL_SEARCH_RECENTS_KEY_PREFIX = 'dentexe.globalSearch.recents.';

/** Idle quick-action tiles (subset). Full command set lives in search-commands.ts */
export const GLOBAL_SEARCH_QUICK_ACTIONS: GlobalSearchActionHit[] = [
  {
    kind: 'action',
    id: 'new-patient',
    label: 'New patient',
    description: 'Register someone new',
    href: '/patients/new',
    icon: 'userPlus',
  },
  {
    kind: 'action',
    id: 'book-appointment',
    label: 'Book appointment',
    description: 'Schedule a visit',
    href: '/appointments/new',
    icon: 'calendar',
  },
  {
    kind: 'action',
    id: 'waiting-room',
    label: 'Waiting room',
    description: 'Open the live floor queue',
    href: '/appointments/waiting-room',
    icon: 'users',
  },
  {
    kind: 'action',
    id: 'all-invoices',
    label: 'All invoices',
    description: 'Open billing invoices',
    href: '/billing/invoices',
    icon: 'receipt',
  },
];

/** Minimum characters before we fan out network searches (efficiency). */
export const GLOBAL_SEARCH_MIN_CHARS = 2;
export const GLOBAL_SEARCH_DEBOUNCE_MS = 320;
export const GLOBAL_SEARCH_PATIENT_LIMIT = 5;
export const GLOBAL_SEARCH_APPOINTMENT_LIMIT = 5;
export const GLOBAL_SEARCH_INVOICE_LIMIT = 4;
